import React, { MouseEvent } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { RiTwitterXFill } from 'react-icons/ri';
import {useLanguage} from "@/contexts/LanguageContext.tsx";

const FooterWrapper = styled.footer`
  background-color: var(--bg-primary);
  color: white;
  padding: 4rem 0 2rem 0;
  width: 100%;
  position: relative;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: var(--z-sticky);
  display: block;
  box-shadow: 0 -5px 15px rgba(0,0,0,0.1);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2.5rem;

  @media (max-width: 768px) {
    gap: 2rem;
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled.div`
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h3`
  color: #FFD700;
  margin-bottom: 1.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const FooterLinks = styled.div`
  padding: 0;
`;

const FooterLink = styled(Link)`
  display: block;
  color: #fff;
  text-decoration: none;
  transition: all 0.3s ease;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  
  &:hover {
    color: var(--primary-color);
    transform: translateX(4px);
  }
`;

const FooterText = styled.p`
  display: block;
  color: #fff;
  margin-bottom: 0.75rem;
  font-size: 1rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SocialLink = styled.a`
  color: white;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: var(--primary-color);
    background-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-3px);
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #888;
  font-size: 0.875rem;
`;

const LegalLinks = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
`;

const LegalLink = styled(Link)`
  color: #888;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--primary-color);
  }
`;

// Wrapper pour les liens qui scrolle au début de page
const ScrollToTopLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    // Approche plus directe: stocker le fait qu'on veut scroller
    sessionStorage.setItem('scrollToTop', 'true');
    
    // Navigation immédiate
    navigate(to);
  };
  
  return (
    <FooterLink onClick={handleClick} to={to}>
      {children}
    </FooterLink>
  );
};

// Version du ScrollToTopLink pour les liens légaux
const ScrollToTopLegalLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    // Même approche que ScrollToTopLink
    sessionStorage.setItem('scrollToTop', 'true');
    
    // Navigation immédiate
    navigate(to);
  };
  
  return (
    <LegalLink onClick={handleClick} to={to}>
      {children}
    </LegalLink>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  return (
    <FooterWrapper id="site-footer">
      <FooterContent>
        <FooterSection>
          <SectionTitle>{t('footer.about.title')}</SectionTitle>
          <FooterLinks>
            <ScrollToTopLink to="/notre-histoire">{t('footer.about.ourStory')}</ScrollToTopLink>
            <ScrollToTopLink to="/notre-flotte">{t('footer.about.ourVehicles')}</ScrollToTopLink>
            <ScrollToTopLink to="/notre-equipe">{t('footer.about.ourTeam')}</ScrollToTopLink>
            <ScrollToTopLink to="/contact">{t('footer.about.contact')}</ScrollToTopLink>
          </FooterLinks>
        </FooterSection>
        
        <FooterSection>
          <SectionTitle>{t('footer.services.title')}</SectionTitle>
          <FooterLinks>
            <ScrollToTopLink to="/services/transport-standard">{t('footer.services.standard')}</ScrollToTopLink>
            <ScrollToTopLink to="/services/transferts-aeroports-gares">{t('footer.services.airport')}</ScrollToTopLink>
            <ScrollToTopLink to="/services/transfert-colis">{t('footer.services.parcel')}</ScrollToTopLink>
          </FooterLinks>
        </FooterSection>
        
        <FooterSection>
          <SectionTitle>{t('footer.contact.title')}</SectionTitle>
          <FooterLinks>
            <FooterText>{t('footer.contact.email')}: contact@taximoove.com</FooterText>
            <FooterText>{t('footer.contact.phone')}: +32 470 48 56 09</FooterText>
          </FooterLinks>
          <SocialLinks>
            <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer">
              <FaFacebookF />
            </SocialLink>
            <SocialLink href="https://twitter.com" target="_blank" rel="noopener noreferrer">
              <RiTwitterXFill />
            </SocialLink>
            <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer">
              <FaInstagram />
            </SocialLink>
            <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
              <FaLinkedinIn />
            </SocialLink>
          </SocialLinks>
        </FooterSection>
        
        <FooterSection>
          <SectionTitle>{t('footer.join.title')}</SectionTitle>
          <FooterLinks>
            <ScrollToTopLink to="/devenir-chauffeur">{t('footer.join.becomeDriver')}</ScrollToTopLink>
          </FooterLinks>
        </FooterSection>
      </FooterContent>
      <Copyright>
        © {new Date().getFullYear()} TaxiMoove. {t('footer.copyright.allRightsReserved')}
        <LegalLinks>
          <ScrollToTopLegalLink to="/mentions-legales">{t('footer.copyright.mentions')}</ScrollToTopLegalLink>
          <ScrollToTopLegalLink to="/conditions-generales">{t('footer.copyright.terms')}</ScrollToTopLegalLink>
          <ScrollToTopLegalLink to="/politique-confidentialite">{t('footer.copyright.confidentiality')}</ScrollToTopLegalLink>
        </LegalLinks>
      </Copyright>
    </FooterWrapper>
  );
};

export default Footer; 